// Reporting's ASSOCIATED ENTITIES section (owner, 2026-11-04: "build an
// Associated Entities. Let's focus for now on one report: Spend Per Vehicle
// Location. ... when a vehicle tag is saved, we take a 'frozen in time' copy of
// that vehicle record, including its 'department' or location. I want to see
// that information for tags currently active." Then: "Also add there Spend Per
// Vehicle", and a report telling whether stored tags break "A line item can
// only have one associated entity. 'A tag on the whole invoice' is not a
// possibility that exists.")
//
// Three charts. Their reads (`docs/api.md` §16.11–§16.13) are ones the
// screen's other charts do not make, so their data is its own
// (`EntityChartData`, read by `useEntityChartData`) rather than a field of
// `ChartData`. `entityReportCharts` binds that data into plain `ReportChart`s,
// so the rail, the workspace, Compare and Download draw them like any other.
//
// THE TWO SPEND CHARTS count the same tags with the same shares (§16.11 owns
// the rules): active Vehicle tags on the invoice header or an active real
// line, each dollar at most once. So their totals agree. No criterion names a
// location, a vehicle or an exception, so no bar is a filter.
//
// No I/O here: `entityChartData.ts` does the reads.
import type {
  AnalyticsSpendByVehicle,
  AnalyticsSpendByVehicleLocation,
  AnalyticsTagExceptionRow,
  AnalyticsTagExceptions,
  AnalyticsVehicleLocationRow,
  AnalyticsVehicleSpendRow,
} from '../api/types'
import type { BarRowData } from '../components/DashboardCharts'
import { statusLabel } from './constants'
import { NO_KEY, compactMoney, wholeNumber, type ReportChart } from './reportCharts'
import { SECTION_COLORS } from './theme'

/** Every read the Associated Entities charts draw from. A failed read is `null`. */
export interface EntityChartData {
  vehicleLocations: AnalyticsSpendByVehicleLocation | null
  vehicles: AnalyticsSpendByVehicle | null
  tagExceptions: AnalyticsTagExceptions | null
}

/** A `ReportChart` over `EntityChartData` instead of `ChartData`. */
interface EntityChart extends Omit<ReportChart, 'rows' | 'truncated'> {
  rows: (data: EntityChartData) => BarRowData[] | null
  truncated: (data: EntityChartData) => boolean
}

/** The section's heading in the Reports rail. */
const ENTITY_GROUP = 'Associated Entities'

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** Bars largest first, ties by label, the catch-all (`NO_KEY`) last among equals. */
function largestFirst(a: BarRowData, b: BarRowData): number {
  return b.total - a.total || (a.key === NO_KEY ? 1 : 0) - (b.key === NO_KEY ? 1 : 0) || a.label.localeCompare(b.label)
}

/** A spend bar of one colour. */
function spendBar(key: string, label: string, total: number, title: string): BarRowData {
  return { key, label, total, display: compactMoney(total), segments: [{ color: SECTION_COLORS.spend, value: total }], title }
}

/**
 * The locations as bars. A location with no spend is left out, as on every
 * spend chart. No Location is keyed `NO_KEY`, which no bar click acts on.
 */
function vehicleLocationBars(read: AnalyticsSpendByVehicleLocation | null): BarRowData[] | null {
  if (read === null) return null
  const label = (row: AnalyticsVehicleLocationRow) => row.location ?? 'No Location'
  return read.rows
    .filter((row) => row.total > 0)
    .map((row) => {
      const vehicles = row.vehicle_count > 0 ? `, ${plural(row.vehicle_count, 'vehicle', 'vehicles')}` : ''
      return spendBar(
        row.location ?? NO_KEY,
        label(row),
        row.total,
        `${label(row)}: ${compactMoney(row.total)} across ${plural(row.invoice_count, 'invoice', 'invoices')}${vehicles}`,
      )
    })
    .sort(largestFirst)
}

/**
 * A vehicle as its saved copy names it: the fleet number, then make and model
 * where the copy has them ("24 · Ford Transit"). The tags matched to no
 * vehicle are No Vehicle.
 */
function vehicleLabel(row: Pick<AnalyticsVehicleSpendRow, 'vehicle_id' | 'number' | 'make' | 'model'>): string {
  if (row.vehicle_id === null) return 'No Vehicle'
  const kind = [row.make, row.model].filter((part) => part !== null && part.trim() !== '').join(' ')
  const number = row.number ?? 'Vehicle With No Saved Copy'
  return kind === '' ? number : `${number} · ${kind}`
}

/** The vehicles as bars; No Vehicle is keyed `NO_KEY`. */
function vehicleBars(read: AnalyticsSpendByVehicle | null): BarRowData[] | null {
  if (read === null) return null
  return read.rows
    .filter((row) => row.total > 0)
    .map((row) =>
      spendBar(
        row.vehicle_id ?? NO_KEY,
        vehicleLabel(row),
        row.total,
        `${vehicleLabel(row)}: ${compactMoney(row.total)} across ${plural(row.invoice_count, 'invoice', 'invoices')}`,
      ),
    )
    .sort(largestFirst)
}

/** An invoice as a person finds it: "<vendor> · <invoice number> · <company>". */
function exceptionLabel(row: AnalyticsTagExceptionRow): string {
  return `${row.vendor_name ?? 'No Vendor'} · ${row.external_invoice_id ?? 'No Invoice Number'} · ${row.company_name ?? 'No Company'}`
}

/** Each count in a full sentence, and where the invoice is now. */
function exceptionTitle(row: AnalyticsTagExceptionRow): string {
  const lines = plural(row.lines_with_several_tags, 'line carries', 'lines carry')
  const loose = plural(row.tags_without_line, 'tag is', 'tags are')
  return `${exceptionLabel(row)}: ${lines} more than one tag, and ${loose} on the whole invoice rather than on a line. It is at ${statusLabel(row.status)}.`
}

/** One bar per affected invoice, its value both counts summed. */
function tagExceptionBars(read: AnalyticsTagExceptions | null): BarRowData[] | null {
  if (read === null) return null
  return read.rows
    .map((row) => {
      const total = row.lines_with_several_tags + row.tags_without_line
      return {
        key: row.invoice_id,
        label: exceptionLabel(row),
        total,
        display: wholeNumber(total),
        segments: [{ color: SECTION_COLORS.feedback, value: total }],
        title: exceptionTitle(row),
      }
    })
    .filter((bar) => bar.total > 0)
    .sort(largestFirst)
}

const ENTITY_CHARTS: readonly EntityChart[] = [
  {
    id: 'entities-vehicle-location-spend',
    group: ENTITY_GROUP,
    title: 'Spend Per Vehicle Location',
    labelHeader: 'Location',
    valueHeader: 'Spend',
    rows: (data) => vehicleLocationBars(data.vehicleLocations),
    truncated: (data) => data.vehicleLocations?.truncated === true,
  },
  {
    id: 'entities-vehicle-spend',
    group: ENTITY_GROUP,
    title: 'Spend Per Vehicle',
    labelHeader: 'Vehicle',
    valueHeader: 'Spend',
    rows: (data) => vehicleBars(data.vehicles),
    truncated: (data) => data.vehicles?.truncated === true,
  },
  {
    id: 'entities-tag-exceptions',
    group: ENTITY_GROUP,
    title: 'Tag Exceptions',
    labelHeader: 'Invoice',
    valueHeader: 'Exceptions',
    rows: (data) => tagExceptionBars(data.tagExceptions),
    truncated: (data) => data.tagExceptions?.truncated === true,
  },
]

/**
 * The charts as plain `ReportChart`s over `data`: `rows` and `truncated`
 * ignore the `ChartData` they are handed and read `data` instead. `null` data
 * (not read yet, or no filters) draws as a failed read, `null` rows.
 */
export function entityReportCharts(data: EntityChartData | null): ReportChart[] {
  return ENTITY_CHARTS.map((chart) => ({
    ...chart,
    rows: () => (data === null ? null : chart.rows(data)),
    truncated: () => data !== null && chart.truncated(data),
  }))
}
